
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const RealEstatePage = () => {
  useEffect(() => {
    // SEO設定
    document.title = '不動産売買事業 | 株式会社伊藤総業 - 住宅・商業・投資用不動産';
    
    // Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "不動産売買事業 - 株式会社伊藤総業",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/real-estate`,
      "description": "株式会社伊藤総業の不動産売買事業。住宅用・商業用・投資用不動産の売買仲介サービスを提供。",
      "mainEntity": {
        "@type": "Service",
        "name": "不動産売買事業",
        "provider": {
          "@type": "Organization",
          "name": "株式会社伊藤総業"
        },
        "serviceType": "不動産売買",
        "areaServed": {
          "@type": "Place",
          "name": "東京都世田谷区"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
      <Header />
      
      {/* Hero Section */}
      <main>
        <section 
          className="relative h-96 flex items-center justify-center bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/913074c410374657bd4ec09fdca6d2ce.jpg')`
          }}
        >
          <div className="container mx-auto px-4 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              不動産売買事業
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              お客様の大切な資産取引を<br />
              安心・安全にサポート
            </p>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">サービス内容</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                不動産の売買に関する総合的なサービスを提供
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-home-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">不動産売却</h3>
                <p className="text-gray-600">
                  適正価格での不動産売却をサポート。市場分析から契約まで一貫してお手伝いします。
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-key-line text-green-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">不動産購入</h3>
                <p className="text-gray-600">
                  お客様のご希望に合った物件探しから購入手続きまで、専門スタッフがサポートします。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Property Types */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">取扱物件</h2>
              <p className="text-xl text-gray-600">
                様々なタイプの不動産取引に対応
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="h-48 bg-cover bg-center" style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20residential%20house%20exterior%20with%20beautiful%20garden%2C%20family%20home%20real%20estate%20property%2C%20suburban%20residential%20architecture%2C%20quality%20housing%20development&width=400&height=300&seq=residential-property&orientation=landscape')`
                }}></div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">住宅用不動産</h3>
                  <p className="text-gray-600 mb-4">
                    一戸建て、マンション、土地など、住宅用不動産の売買を取り扱います。
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 新築・中古一戸建て</li>
                    <li>• 分譲・中古マンション</li>
                    <li>• 住宅用土地</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="h-48 bg-cover bg-center" style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Commercial%20office%20building%20exterior%2C%20modern%20business%20property%20real%20estate%2C%20commercial%20real%20estate%20investment%2C%20professional%20office%20complex%20architecture&width=400&height=300&seq=commercial-property&orientation=landscape')`
                }}></div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">商業用不動産</h3>
                  <p className="text-gray-600 mb-4">
                    オフィスビル、店舗、倉庫など、商業用不動産の売買をサポートします。
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• オフィスビル</li>
                    <li>• 店舗・商業施設</li>
                    <li>• 倉庫・工場</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="h-48 bg-cover bg-center" style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Investment%20property%20portfolio%20analysis%2C%20real%20estate%20investment%20planning%2C%20property%20investment%20consultation%2C%20financial%20real%20estate%20planning&width=400&height=300&seq=investment-property&orientation=landscape')`
                }}></div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">投資用不動産</h3>
                  <p className="text-gray-600 mb-4">
                    収益性を重視した投資用不動産の売買をお手伝いします。
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 賃貸マンション</li>
                    <li>• アパート</li>
                    <li>• 収益ビル</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="h-96 bg-cover bg-center rounded-xl" style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20real%20estate%20consultation%20meeting%2C%20real%20estate%20agent%20consulting%20with%20clients%2C%20property%20investment%20advice%20session%2C%20real%20estate%20planning%20discussion&width=600&height=400&seq=real-estate-consultation&orientation=landscape')`
              }}></div>
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">選ばれる理由</h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-user-star-line text-blue-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">専門知識</h4>
                      <p className="text-gray-600">
                        豊富な経験と専門知識を持つスタッフが、お客様の不動産取引をサポートします。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-search-line text-green-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">市場分析</h4>
                      <p className="text-gray-600">
                        詳細な市場分析により、適正な価格での売買をご提案いたします。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <i className="ri-shield-check-line text-purple-600 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">安心取引</h4>
                      <p className="text-gray-600">
                        法的手続きから契約まで、安心・安全な取引をお約束します。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">取引の流れ</h2>
              <p className="text-xl text-gray-600">
                スムーズで安心な不動産取引をサポート
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* 売却の流れ */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">売却の流れ</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">査定依頼</h4>
                      <p className="text-gray-600 text-sm">物件の査定をご依頼ください</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">現地調査</h4>
                      <p className="text-gray-600 text-sm">専門スタッフが現地調査を実施</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">価格提示</h4>
                      <p className="text-gray-600 text-sm">市場価格に基づく査定額をご提示</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      4
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">媒介契約</h4>
                      <p className="text-gray-600 text-sm">売却活動開始のための契約締結</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      5
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">売却活動</h4>
                      <p className="text-gray-600 text-sm">広告・営業活動で買主を探索</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      6
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">契約・引渡し</h4>
                      <p className="text-gray-600 text-sm">売買契約締結と物件引渡し</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 購入の流れ */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">購入の流れ</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">ご相談</h4>
                      <p className="text-gray-600 text-sm">ご希望条件をお聞かせください</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">物件紹介</h4>
                      <p className="text-gray-600 text-sm">条件に合った物件をご紹介</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">現地見学</h4>
                      <p className="text-gray-600 text-sm">実際に物件をご見学いただきます</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      4
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">購入申込</h4>
                      <p className="text-gray-600 text-sm">購入意思表示と条件交渉</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      5
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">契約手続き</h4>
                      <p className="text-gray-600 text-sm">重要事項説明と売買契約締結</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center mr-4 flex-shrink-0 text-sm font-bold">
                      6
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">決済・引渡し</h4>
                      <p className="text-gray-600 text-sm">残金決済と物件引渡し</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              不動産のことなら何でもご相談ください
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              売却・購入に関するご質問やご相談は、お気軽にお問い合わせください
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="bg-white text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                お問い合わせフォーム
              </Link>
              <a 
                href="tel:070-4228-3984" 
                className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                070-4228-3984
              </a>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default RealEstatePage;
